import React, { useEffect, useMemo, useState } from "react";
import { parseDate } from "@/lib/dates";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Clock, CheckCircle2, AlertCircle, CreditCard, ArrowRight, ArrowLeft,
} from "lucide-react";
import { ForaStyles } from "./TripView";
import { loadTracking, trackOnce } from "@/lib/tracking";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Currency-aware formatter mirroring PublicPayment.jsx (2026-09-14 fix).
const fmtUSD = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n || 0);
const fmtEUR = (n) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency", currency: "EUR",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n || 0);

const rangeShort = (a, b) => {
  if (!a || !b) return "";
  try {
    const s = parseDate(a).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const e = parseDate(b).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return `${s} — ${e}`;
  } catch { return `${a} — ${b}`; }
};

/**
 * PublicCuratedPayment — the buyer's personal booking page after they
 * submit the reserve modal on /curated/:token. Renders:
 *   - Trip name + dates + seat breakdown
 *   - Live countdown of the 10-min soft-hold
 *   - "Pay with PayPal" CTA → POST /create-order → redirect to PayPal
 *   - Success state after the PayPal return handler flips status="paid"
 *   - Expired state with a link back to the trip landing
 *
 * URL: /pay/curated/:token  (token is the CuratedBooking.payment_token)
 */
export default function PublicCuratedPayment() {
  const { token } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(null); // holds the kind while creating an order
  // Live countdown to hold_expires_at. Recomputed each second so the
  // buyer always sees the accurate remaining time.
  const [now, setNow] = useState(() => Date.now());
  // Which payment option the buyer currently has selected. Defaulted
  // once data loads (deposit if available, else full).
  const [chosenKind, setChosenKind] = useState(null);

  const successAmount = search.get("amount");
  const successFlag = search.get("success") === "1";
  const successKind = search.get("kind") || "";
  const cancelledFlag = search.get("cancelled") === "1";
  const errorParam = search.get("error");

  const load = async () => {
    try {
      const { data: d } = await axios.get(`${API_BASE}/curated/booking/${token}`);
      setData(d);
      // Sensible default: deposit if it's an option, otherwise full.
      const kinds = (d?.payment_options?.options || []).map((o) => o.kind);
      if (!chosenKind) {
        setChosenKind(kinds.includes("deposit") ? "deposit" : (kinds[0] || "full"));
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "This reservation link is not available.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);
  useEffect(() => { loadTracking(); }, []);
  // Purchase conversion — once per capture (PayPal bounces back with
  // ?success=1&amount=…); sessionStorage keeps a refresh from double-firing.
  useEffect(() => {
    if (!data || !successFlag || !successAmount) return;
    trackOnce(`purchase_${token}_${successKind}_${successAmount}`, "purchase", {
      content_id: data.public_token, content_name: data.trip_name,
      value: parseFloat(successAmount), currency: data.currency, quantity: data.seats,
      transaction_id: `${data.booking_id}-${successKind || "full"}-${successAmount}`,
      event_id: `purchase-${token}-${successKind}-${successAmount}`,
    });
    // eslint-disable-next-line
  }, [data, successFlag, successAmount]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const currency = (data?.currency || "USD").toUpperCase();
  const fmt = (n) => (currency === "EUR" ? fmtEUR(n) : fmtUSD(n));

  const remainingMs = useMemo(() => {
    if (!data?.hold_expires_at) return 0;
    return new Date(data.hold_expires_at).getTime() - now;
  }, [data?.hold_expires_at, now]);
  const remainingSec = Math.max(0, Math.floor(remainingMs / 1000));
  const mm = String(Math.floor(remainingSec / 60)).padStart(2, "0");
  const ss = String(remainingSec % 60).padStart(2, "0");

  const isFullyPaid = data?.status === "paid";
  const isSecured = data?.status === "secured";
  // We consider "hold expired" only when NO payment has been captured
  // yet. A secured booking (deposit paid) never expires.
  const isExpired = !isFullyPaid && !isSecured && (
    data?.status === "expired" ||
    data?.status === "cancelled" ||
    (data?.status === "hold" && remainingSec === 0 && data?.hold_expires_at)
  );
  const paymentOptions = data?.payment_options?.options || [];
  const paid = data?.payment_options?.paid_eur || 0;
  const remaining = data?.payment_options?.remaining_eur ?? data?.total_amount ?? 0;

  const handlePay = async (kind) => {
    setSubmitting(kind);
    setError(null);
    try {
      const { data: order } = await axios.post(
        `${API_BASE}/curated/booking/${token}/create-order`,
        { origin: window.location.origin, kind },
      );
      if (order?.approval_url) {
        window.location.href = order.approval_url;
      } else {
        setError("Could not start the PayPal checkout. Please try again.");
        setSubmitting(null);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "PayPal could not be reached. Please try again in a moment.");
      setSubmitting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center text-ink/70 font-sans">
        <Loader2 size={16} className="animate-spin mr-2"/> Loading your reservation…
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <h1 className="font-serif text-3xl text-ink">Reservation not found</h1>
          <p className="mt-4 font-sans text-ink/70">{error}</p>
        </div>
      </div>
    );
  }

  // Seat summary: group room selections by type for readable copy.
  const roomBreakdown = (data?.room_selections || []).reduce((acc, r) => {
    acc[r.room_type] = (acc[r.room_type] || 0) + 1;
    return acc;
  }, {});
  const roomLine = Object.entries(roomBreakdown)
    .map(([k, n]) => `${n} × ${k === "single" ? "Single (private)" : "Double (shared)"}`)
    .join(" · ");

  return (
    <div className="min-h-screen bg-cream text-ink font-sans">
      <CuratedPayStyles/>
      <ForaStyles/>
      {/* Header */}
      <header className="border-b border-ink/10 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <img
            src="/espiritu/logo-horizontal.png"
            alt="Espíritu Travel"
            className="h-7 w-auto"
          />
          <Link
            to={`/curated/${data?.public_token || ""}`}
            data-testid="back-to-trip"
            className="inline-flex items-center gap-1.5 text-[13px] text-ink/60 hover:text-ink"
          >
            <ArrowLeft size={13}/> Back to trip
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-14 sm:py-20">
        {/* Fully paid — success card */}
        {isFullyPaid ? (
          <div className="bg-white border border-ink/10 rounded-xl p-8 sm:p-10 text-center" data-testid="paid-state">
            <div className="w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 size={26} className="text-emerald-600"/>
            </div>
            <div className="kicker mb-2">Fully paid</div>
            <h1 className="font-serif text-3xl sm:text-4xl text-ink">
              You&apos;re all set, {data?.buyer_name?.split(" ")[0] || "traveller"}.
            </h1>
            <p className="mt-4 font-sans text-ink/70">
              Your full amount of{" "}
              <strong className="text-ink" data-testid="paid-amount">
                {fmt(data?.total_amount)}
              </strong>{" "}
              for <em>{data?.trip_name}</em> has been captured.
              A confirmation is on its way to <span className="text-ink">{data?.buyer_email}</span>.
            </p>
            <div className="mt-6 text-[13px] text-ink/50 font-sans">
              Confirmation reference: <span className="tabular text-ink/70">{data?.booking_id}</span>
            </div>
          </div>
        ) : isExpired ? (
          /* Expired — timeout state */
          <div className="bg-white border border-ink/10 rounded-xl p-8 sm:p-10 text-center" data-testid="expired-state">
            <div className="w-14 h-14 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto mb-5">
              <AlertCircle size={26} className="text-amber-600"/>
            </div>
            <div className="kicker mb-2">Hold expired</div>
            <h1 className="font-serif text-3xl sm:text-4xl text-ink">
              Your seats are no longer held
            </h1>
            <p className="mt-4 font-sans text-ink/70 max-w-md mx-auto">
              The 10-minute reservation window has passed and your seats are back in the pool.
              If they&apos;re still available, you can start a fresh reservation from the trip page.
            </p>
            {data?.public_token && (
              <Link
                to={`/curated/${data.public_token}`}
                data-testid="restart-reservation"
                className="mt-8 inline-flex items-center gap-2 bg-ink hover:bg-black text-white px-6 py-3 rounded-full text-sm font-sans font-medium"
              >
                Try again <ArrowRight size={14}/>
              </Link>
            )}
          </div>
        ) : (
          /* Hold OR secured — buyer picks a payment option */
          <div>
            <div className="text-center mb-10" data-testid={isSecured ? "secured-state" : "hold-active"}>
              {isSecured ? (
                <>
                  <div className="inline-flex items-center gap-2 kicker text-emerald-700 mb-3">
                    <CheckCircle2 size={12}/> Booking confirmed
                  </div>
                  <h1 className="font-serif text-3xl sm:text-4xl text-ink">
                    Your seats are secured, {data?.buyer_name?.split(" ")[0] || "traveller"}.
                  </h1>
                  <p className="mt-4 font-sans text-ink/70 max-w-lg mx-auto">
                    Deposit received. Complete the balance any time before 60 days from departure
                    to keep your reservation.
                  </p>
                </>
              ) : (
                <>
                  <div className="kicker mb-3">Reservation on hold</div>
                  <h1 className="font-serif text-3xl sm:text-4xl text-ink">
                    Choose how you&apos;d like to pay
                  </h1>
                </>
              )}
            </div>

            {/* Countdown pill — only while hold is active */}
            {!isSecured && data?.hold_expires_at && (
              <div className="flex justify-center mb-8">
                <div
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border font-sans text-[14px] tabular ${
                    remainingSec < 60
                      ? "bg-red-50 border-red-200 text-red-700"
                      : "bg-amber-50 border-amber-200 text-amber-800"
                  }`}
                  data-testid="countdown"
                >
                  <Clock size={14}/>
                  <span>Seats held for {mm}:{ss}</span>
                </div>
              </div>
            )}

            {/* Booking summary card */}
            <div className="bg-white border border-ink/10 rounded-xl p-6 sm:p-8" data-testid="booking-summary">
              <div className="grid sm:grid-cols-2 gap-y-4 gap-x-8 font-sans text-[14px]">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-ink/50">Trip</div>
                  <div className="text-ink mt-1 font-serif text-xl">{data?.trip_name}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-ink/50">Dates</div>
                  <div className="text-ink mt-1">
                    {rangeShort(data?.start_date, data?.end_date)}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-ink/50">Travellers</div>
                  <div className="text-ink mt-1">
                    {data?.seats} · {roomLine}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-ink/50">Buyer</div>
                  <div className="text-ink mt-1">
                    {data?.buyer_name} <span className="text-ink/50">·</span>{" "}
                    <span className="text-ink/70">{data?.buyer_email}</span>
                  </div>
                </div>
              </div>
              {/* Pricing summary */}
              <div className="mt-6 pt-6 border-t border-ink/10 space-y-2 font-sans text-[14px]" data-testid="pricing-summary">
                <div className="flex items-baseline justify-between">
                  <span className="text-ink/60">Total</span>
                  <span className="text-ink tabular">{fmt(data?.total_amount)}</span>
                </div>
                {paid > 0 && (
                  <div className="flex items-baseline justify-between">
                    <span className="text-ink/60">Paid so far</span>
                    <span className="text-emerald-700 tabular" data-testid="paid-so-far">
                      − {fmt(paid)}
                    </span>
                  </div>
                )}
                <div className="flex items-baseline justify-between pt-2 border-t border-ink/10">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-ink/50">
                    {paid > 0 ? "Balance due" : "Total due"}
                  </span>
                  <span className="font-serif text-3xl text-ink tabular" data-testid="total-due">
                    {fmt(remaining)}
                  </span>
                </div>
              </div>
            </div>

            {/* Success banner after a partial capture (deposit or balance) */}
            {successFlag && successAmount && !isFullyPaid && (
              <div className="mt-6 p-4 rounded-md bg-emerald-50 border border-emerald-200 text-[13px] text-emerald-900 font-sans flex items-start gap-2" data-testid="partial-success">
                <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-600"/>
                <span>
                  We&apos;ve captured <strong>{fmt(parseFloat(successAmount))}</strong>
                  {successKind === "deposit" && " as your deposit — your seats are secured."}
                  {successKind === "balance" && " as your balance."}
                  {!["deposit", "balance"].includes(successKind) && "."}
                </span>
              </div>
            )}

            {/* Errors from create-order (e.g. PayPal reachability) */}
            {(error || cancelledFlag || errorParam) && (
              <div className="mt-6 p-4 rounded-md bg-red-50 border border-red-200 text-[13px] text-red-800 font-sans flex items-start gap-2" data-testid="pay-error">
                <AlertCircle size={14} className="mt-0.5 shrink-0"/>
                <span>
                  {error ||
                    (cancelledFlag && "Payment cancelled. Your seats are still on hold — you can try again.") ||
                    (errorParam === "capture_failed" && "PayPal could not capture the payment. Please try again.") ||
                    (errorParam === "not_completed" && "PayPal did not complete the payment. Your seats are still held.") ||
                    "Something went wrong. Please try again."}
                </span>
              </div>
            )}

            {/* Payment options — one card per available `kind`.
                We filter out `partial` in V1 because a full custom-amount
                input UX isn't polished yet — buyers get deposit / full /
                balance / complete_deposit which covers 100% of the cases. */}
            <div className="mt-8 space-y-3" data-testid="payment-options">
              {paymentOptions
                .filter((opt) => opt.kind !== "partial")
                .map((opt) => {
                const isChosen = chosenKind === opt.kind;
                const isSubmittingThis = submitting === opt.kind;
                return (
                  <label
                    key={opt.kind}
                    className={`block bg-white border rounded-xl p-5 cursor-pointer transition ${
                      isChosen ? "border-ink shadow-sm" : "border-ink/15 hover:border-ink/30"
                    }`}
                    data-testid={`option-${opt.kind}`}
                  >
                    <div className="flex items-start gap-4">
                      <input
                        type="radio"
                        name="payment-kind"
                        checked={isChosen}
                        onChange={() => setChosenKind(opt.kind)}
                        className="mt-1 accent-ink"
                        data-testid={`option-radio-${opt.kind}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-4">
                          <div className="font-serif text-xl text-ink">
                            {optionLabel(opt.kind)}
                          </div>
                          <div className="font-serif text-2xl text-ink tabular shrink-0" data-testid={`option-amount-${opt.kind}`}>
                            {fmt(opt.amount_eur)}
                          </div>
                        </div>
                        <p className="mt-1.5 font-sans text-[13px] text-ink/60 leading-relaxed">
                          {optionDescription(opt, data, fmt)}
                        </p>
                        {isChosen && (
                          <button
                            onClick={(e) => { e.preventDefault(); handlePay(opt.kind); }}
                            disabled={isSubmittingThis || submitting !== null}
                            data-testid={`pay-${opt.kind}-btn`}
                            className="mt-4 inline-flex items-center gap-2 bg-ink hover:bg-black disabled:bg-ink/25 disabled:cursor-not-allowed text-white px-6 py-3 rounded-full text-[14px] font-sans font-medium transition-colors"
                          >
                            {isSubmittingThis ? (
                              <><Loader2 size={14} className="animate-spin"/> Redirecting to PayPal…</>
                            ) : (
                              <><CreditCard size={14}/> Pay {fmt(opt.amount_eur)} with PayPal <ArrowRight size={14}/></>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            <div className="mt-6 text-center font-sans text-[12px] text-ink/50 max-w-md mx-auto leading-relaxed">
              {isSecured
                ? "Your seat is confirmed. Complete the balance at any time before your travel dates."
                : "Your reservation is confirmed only after PayPal captures the payment. Until then, other travellers may book these seats."}
            </div>
          </div>
        )}

        {/* Extensions block (Fase 6) — pre/post-trip itineraries linked
            to this booking. Shown in every state (paid/secured/hold)
            so the buyer sees all their connected trips in one place. */}
        {data?.extensions && data.extensions.length > 0 && (
          <section className="mt-10" data-testid="extensions-section">
            <div className="kicker mb-3 text-center">Your extensions</div>
            <h2 className="font-serif text-2xl sm:text-3xl text-ink text-center mb-6">
              Trips connected to your reservation
            </h2>
            <div className="space-y-3">
              {data.extensions.map((ext) => {
                const label = ext.extension_kind === "pre" ? "Pre-trip" : "Post-trip";
                const kindKlass = ext.extension_kind === "pre"
                  ? "bg-sky-50 text-sky-800 border-sky-200"
                  : "bg-violet-50 text-violet-800 border-violet-200";
                const linkPay = ext.payment_token ? `/pay/${ext.payment_token}` : null;
                return (
                  <div
                    key={ext.itinerary_id}
                    className="bg-white border border-ink/10 rounded-xl p-5 sm:p-6"
                    data-testid={`extension-card-${ext.itinerary_id}`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium border ${kindKlass}`}>
                          {label}
                        </span>
                        <div className="font-serif text-xl text-ink mt-2">{ext.name}</div>
                        {(ext.start_date || ext.end_date) && (
                          <div className="mt-1 font-sans text-[13px] text-ink/60">
                            {rangeShort(ext.start_date, ext.end_date) || ext.start_date || ext.end_date}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {linkPay && (
                          <Link
                            to={linkPay}
                            data-testid={`extension-open-${ext.itinerary_id}`}
                            className="inline-flex items-center gap-1.5 bg-ink hover:bg-black text-white px-4 py-2 rounded-full text-[13px] font-sans font-medium"
                          >
                            {ext.paid_any ? "View" : "Review & pay"} <ArrowRight size={13}/>
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>

      <footer className="border-t border-ink/10 mt-16">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between font-sans text-[11px] text-ink/50">
          <div>© Espíritu Travel · All rights reserved</div>
          <div>Need help? <a href="mailto:hola@espiritutravel.com" className="underline hover:text-ink">Get in touch</a></div>
        </div>
      </footer>
    </div>
  );
}

function CuratedPayStyles() {
  return (
    <style>{`
      .kicker {
        font-family: ui-sans-serif, system-ui, sans-serif;
        font-size: 10px;
        letter-spacing: 0.22em;
        text-transform: uppercase;
        color: #B08749;
      }
      .tabular { font-variant-numeric: tabular-nums; }
      .bg-cream { background: #F7F3EC; }
      .text-ink { color: #14100c; }
      .font-serif { font-family: 'Cormorant Garamond', 'Playfair Display', Georgia, serif; }
      .font-sans { font-family: 'Inter', ui-sans-serif, system-ui, sans-serif; }
    `}</style>
  );
}

// UI copy per payment option kind. Mirrors the itinerary presentation
// so a client who already booked an itinerary sees the same wording
// on a curated trip.
function optionLabel(kind) {
  switch (kind) {
    case "deposit": return "Reserve with 30% deposit";
    case "full": return "Pay in full";
    case "balance": return "Pay remaining balance";
    case "complete_deposit": return "Complete deposit";
    case "partial": return "Custom partial payment";
    default: return kind;
  }
}

function optionDescription(opt, data, fmt) {
  const total = data?.total_amount || 0;
  switch (opt.kind) {
    case "deposit":
      return `Confirm your seats now. The remaining ${fmt(total - opt.amount_eur)} is due at least 60 days before departure.`;
    case "full":
      return "Single payment for the entire trip — you're all set after this one.";
    case "balance":
      return "Settle what's left on your booking.";
    case "complete_deposit":
      return "Top up the current partial payment to reach the 30% deposit threshold and lock in your seats.";
    case "partial":
      return "Choose any amount between the 10% floor and the outstanding balance.";
    default:
      return "";
  }
}
